import { useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

const IDLE_MINUTES = 15;
const WARN_BEFORE_MS = 60 * 1000;          // warn 1 minute before logout (at 14 min)
const TIMEOUT_MS = IDLE_MINUTES * 60 * 1000;
const ACTIVITY_EVENTS = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'scroll', 'click'];

export function useIdleTimeout({ onWarn, onLogout }) {
  const navigate = useNavigate();
  const logoutTimer = useRef(null);
  const warnTimer = useRef(null);
  const warned = useRef(false);

  const clearTimers = useCallback(() => {
    clearTimeout(logoutTimer.current);
    clearTimeout(warnTimer.current);
  }, []);

  const logout = useCallback(() => {
    clearTimers();
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    onLogout?.();
    navigate('/');
  }, [clearTimers, navigate, onLogout]);

  const resetTimers = useCallback(() => {
    clearTimers();
    warned.current = false;

    warnTimer.current = setTimeout(() => {
      warned.current = true;
      onWarn?.();
    }, TIMEOUT_MS - WARN_BEFORE_MS);

    logoutTimer.current = setTimeout(logout, TIMEOUT_MS);
  }, [clearTimers, logout, onWarn]);

  useEffect(() => {
    // Only run when the user is authenticated
    if (!localStorage.getItem('token')) return;

    resetTimers();
    ACTIVITY_EVENTS.forEach(evt => window.addEventListener(evt, resetTimers, { passive: true }));

    return () => {
      clearTimers();
      ACTIVITY_EVENTS.forEach(evt => window.removeEventListener(evt, resetTimers));
    };
  }, [resetTimers, clearTimers]);
}
